import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { FormBuilder } from '@angular/forms';
import { Observable, Subscription, filter } from 'rxjs';
import * as moment from 'moment';
import { LANGUAGES } from 'src/app/conf/languages';
import { Lang } from 'src/app/model/lang';
import { Permission, PermittedCommand } from 'src/app/model/permission';
import * as PermissionActions from 'src/app/store/permissions/permissions.actions';
import { selectPermission, selectPermittedCommands } from 'src/app/store/permissions/permissions.selector';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
	templateUrl: './permission-info.dialog.component.html',
})

export class PermissionInfoDialogComponent implements OnInit, OnDestroy {
	permittedCommand$: Observable<PermittedCommand[] | undefined>;
	permittedCommandSub: Subscription | undefined;
	permission$: Observable<Permission | undefined>;
	permissionSub: Subscription | undefined;

	permission: Permission;
	permittedCommands: PermittedCommand[] = [];

	actionVisible: boolean;

	languages = LANGUAGES;
	flagImagePath = './assets/images/flags/';
	flagImageFormat = '.svg';

	oldStr: string;

	// design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	MAX_MD_WIDTH = 1120;
	MIN_WIDTH_COL4 = 1680;
	DELTA_ERROR_PIXEL = 7;
	MIN_TWO_BUTTONS_DIALOG_WIDTH = 575 + this.DELTA_ERROR_PIXEL;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.permittedCommand$ = this.store.select(selectPermittedCommands);
		this.permission$ = this.store.select(selectPermission);
	}

	ngOnInit(): void {
		if (this.config.data.permission) {
			this.permission = { ...this.config.data.permission };
			this.store.dispatch(PermissionActions.getPermission({ permissionId: this.permission.Id }));
		} else {
			this.permission = new Permission();
		}

		this.subscribeAll();
		this.onResize();
	}

	ngOnDestroy(): void {
		this.permissionSub?.unsubscribe();
		this.permittedCommandSub?.unsubscribe();
	}

	subscribeAll(): void {
		this.permissionSub = this.permission$?.pipe(
			filter(p => !!p)
		)
			.subscribe(p => {
				this.permission = p;
			}
			);
		this.permittedCommandSub = this.permittedCommand$.subscribe(permittedCommands => {
			this.permittedCommands = permittedCommands;
			// this.createGroupPermittedCommands();
		});
	}

	setupCommandsForm(): void {
		/* this.permissionSub = this.permission$.pipe(filter(cmd => !!cmd)).subscribe(permission => {
			this.selCommands.setValue(this.selectCommands(permission.Commands));
		}); */
	}

	// permission.Commands have the Id field, while permittedCommands (used for the groupedComands list) have no Id
	selectCommands(commands: PermittedCommand[]): PermittedCommand[] {
		const cmds: PermittedCommand[] = [];
		commands.forEach(c => {
			const cmd = this.permittedCommands.find(pc => pc.DeviceId === c.DeviceId && pc.CommandId === c.CommandId);
			if (cmd) {
				cmds.push(cmd);
			}
		});
		return cmds;
	}

	setFirstDeviceName(cmdDevName: string): string {
		this.oldStr = cmdDevName;
		return cmdDevName;
	}

	getGroup(cmd): boolean {
		if (cmd.DeviceName !== this.oldStr) {
			this.oldStr = cmd.DeviceName;
			return true;
		} else {
			return false;
		}
	}

	cleanUndefinedValue(obj: Permission) {
		Object.keys(obj).forEach(key => obj[key] === undefined ? delete obj[key] : {});
	}

	getTime(date: Date): string {
		return moment(date).format('HH:mm');
	}

	getLanguage(code: Lang["code"]): string {
		let langName = '';
		LANGUAGES.forEach(lang => {
			if (code === lang.code) {
				langName = lang.name;
			}
		});
		return langName;
	}

	getColorStatus(status: string): string {
		switch (status) {
			case 'expired':
				return 'danger';
			case 'active':
				return 'success';
			case 'ready':
				return 'warning';
			default:
				return 'info';
		}
	}

	getCurrentLanguageFlag(customerLanguage): string {
		const currentLangFlagFile = this.flagImagePath + customerLanguage + this.flagImageFormat;
		return currentLangFlagFile;
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onMobile(): boolean {
		return this.screenWidth <= this.MIN_MD_WIDTH;
	}

	col12Responsive(): string {
		return this.screenWidth <= this.MAX_MD_WIDTH ? 'col-12' : 'col-6';
	}

	dynamicButtonWidth(): string {
		return this.screenWidth <= this.MIN_TWO_BUTTONS_DIALOG_WIDTH
			? 'w-full'
			: 'w-16rem';
	}

	justifyContentStyle(): string {
		return this.onMobile() ? "justify-content-center" : "justify-content-start";
	}

	dynamicInputTextFormat(classType: string): string {
		switch (classType) {
			case 'borderRow':
				if (this.screenWidth <= this.MIN_MD_WIDTH) {
					return "row";
				}
				return "";
			case 'borderInput':
				if (this.screenWidth <= this.MIN_MD_WIDTH) {
					return "col-12 w-full p-0";
				}
				return "";
			case 'label':
				if (this.screenWidth <= this.MIN_MD_WIDTH) {
					return "col-12 p-1";
				}
				return "";
			case 'inputText':
				if (this.screenWidth <= this.MAX_MD_WIDTH) {
					if (this.screenWidth <= this.MIN_MD_WIDTH) {
						return "w-full h-2_5rem"
					}
					return "w-20rem h-2rem";
				}
				return "w-20rem h-2rem";
			default:
				return "";
		}
	}
}
